import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { Dialog, DialogContent, DialogTitle } from '@artstorefronts/ui';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

import { FETCH_MOCKS } from '../../../graphql/queries';
import Mock from '../../Mock';
import { useAuth } from '../../../context/auth';
import { useLibraryImage } from '../../../hooks/useLibraryImages';
import { TranslatedCalling } from './common';
import { ImageDimensions, MockDimensions } from '../../../types';

function parseMockId(url: string): string {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/').filter(Boolean);

    if (pathParts.length >= 3) {
      return pathParts[1];
    }
  } catch (error) {
    console.warn('Failed to parse mockId from URL:', error);
  }

  return '';
}

function parseImageId(url: string): string {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/').filter(Boolean);

    if (pathParts.length >= 3) {
      return pathParts[2];
    }
  } catch (error) {
    console.warn('Failed to parse imageId from URL:', error);
  }

  return '';
}

function FetchRoomMocksResult({ toolCall }: { toolCall: any }) {
  const { t } = useTranslation('chat');
  const { user } = useAuth();
  const images = toolCall.result || [];
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  const mockUuids = [...new Set(images.map((url: string) => parseMockId(url)).filter(Boolean))];

  const { data: mocksData, loading: mocksLoading } = useQuery(FETCH_MOCKS, {
    variables: { uuids: mockUuids },
    skip: mockUuids.length === 0,
  });

  const mocksMap = new Map();
  if (mocksData?.mocks) {
    mocksData.mocks.forEach((mock: any) => {
      mocksMap.set(mock.uuid, mock);
    });
  }

  const selectedImageUrl = selectedImageIndex !== null ? images[selectedImageIndex] : null;
  const selectedMockUuid = selectedImageUrl ? parseMockId(selectedImageUrl) : null;
  const selectedMock = selectedMockUuid ? mocksMap.get(selectedMockUuid) : null;
  const selectedImageId = selectedImageUrl ? parseImageId(selectedImageUrl) : null;

  const selectedImageData = useLibraryImage(selectedImageId);

  const imageDimensions: ImageDimensions = {
    imageWidth: selectedImageData?.width,
    imageHeight: selectedImageData?.height,
    imageDimensionType: selectedImageData?.dimensionType || 'inches',
  };

  const mockDimensions: MockDimensions = {
    mockScaleFactor: selectedMock?.scaleFactor || 1,
  };

  return (
    <>
      <div className="text-xs text-gray-400 font-light flex items-center gap-1">
        {mocksLoading ? (
          <div className="text-xs text-gray-500">{t('tool.fetch-room-mocks.loading-mockup-data')}</div>
        ) : images.length > 0 ? (
          images.map((src: string, index: number) => (
            <button
              key={index}
              onClick={() => setSelectedImageIndex(index)}
              className="hover:opacity-80 transition-opacity cursor-pointer"
              title={t('tool.fetch-room-mocks.click-to-view', { number: index + 1 })}
            >
              <img
                src={src}
                alt={t('tool.fetch-room-mocks.room-mock-alt', { number: index + 1 })}
                className="w-10 h-10 rounded-md object-cover"
              />
            </button>
          ))
        ) : (
          <div className="text-xs text-red-500">{t('tool.fetch-room-mocks.no-images-found')}</div>
        )}
      </div>

      <Dialog
        open={selectedImageIndex !== null && !!selectedMock && !!selectedImageId && !!user}
        onOpenChange={(open) => !open && setSelectedImageIndex(null)}
      >
        <VisuallyHidden>
          <DialogTitle>{t('tool.fetch-room-mocks.room-mockup')}</DialogTitle>
        </VisuallyHidden>
        <DialogContent
          className="sm:w-[min(80vw,80vh)] aspect-square max-w-none max-h-none p-0 border-0 bg-transparent overflow-hidden"
          aria-describedby={undefined}
        >
          <div className="w-full h-full relative">
            {selectedImageIndex !== null && selectedMock && selectedImageId && user && (
              <Mock
                asfId={user.user_id}
                mock={selectedMock}
                imageId={parseInt(selectedImageId)}
                onClose={() => setSelectedImageIndex(null)}
                imageDimensions={imageDimensions}
                mockDimensions={mockDimensions}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export const fetch_room_mocks = {
  type: 'acting',
  call: () => <TranslatedCalling i18nKey="tool.fetch-room-mocks.creating" />,
  'partial-call': () => <TranslatedCalling i18nKey="tool.fetch-room-mocks.creating" />,
  result: ({ toolCall }: any) => <FetchRoomMocksResult toolCall={toolCall} />,
};
